/** Short premium hints for progressive configurator steps. */

export type StepCopy = { title: string; hint: string; icon: string };

export type StepCopyOverrides = Partial<Record<string, Partial<StepCopy>>>;

export const CFG_STEP_COPY: Record<string, StepCopy> = {
  tier: {
    icon: '✦',
    title: 'Профиль исполнения',
    hint: 'Standard — базовая несущая способность и стандартный шум. Premium — усиленный профиль и запас по нагрузке для широких проёмов.',
  },
  power: {
    icon: '⚡',
    title: 'Питание мотора',
    hint: '220В — постоянная сеть, максимальная тяга и любые длины. АКБ — автономный мотор на батарее, обычно ZigBee; для длинных штор выбирайте сеть.',
  },
  opening: {
    icon: '↔',
    title: 'Направление открывания',
    hint: 'К центру — две створки навстречу. Влево / вправо — одна створка уезжает в сторону. Влияет на комплектацию направляющих.',
  },
  control: {
    icon: '📡',
    title: 'Управление и подключение',
    hint: 'Протокол умного дома — один из Wi‑Fi / ZigBee / Matter. Фаза без Wi‑Fi — мотор MR‑2234F4. Фаза + RS‑485 / TUYA — линейка MR‑2234F5.',
  },
  profile_color: {
    icon: '🎨',
    title: 'Цвет профиля',
    hint: 'Цвет алюминиевой шины. Подбирается под потолок и стены — в прайсе отдельные позиции для белого и чёрного.',
  },
  motor_color: {
    icon: '⚙',
    title: 'Цвет мотора',
    hint: 'Корпус привода в зоне видимости. Белый и чёрный — в каталоге; серебро — там, где указано в матрице.',
  },
  size: {
    icon: '📏',
    title: 'Размер проёма',
    hint: 'Ширина в метрах по прайсу. Цена внизу пересчитается сразу после выбора длины.',
  },
};

function readStepCopyOverrides(raw: Record<string, unknown> | null | undefined): StepCopyOverrides {
  const block = raw?.step_copy;
  if (!block || typeof block !== 'object' || Array.isArray(block)) return {};
  return block as StepCopyOverrides;
}

/** Админ: option_schema.step_copy; пустые поля — дефолт из CFG_STEP_COPY. */
export function parseStepCopyOverrides(raw: Record<string, unknown> | undefined | null): StepCopyOverrides {
  return readStepCopyOverrides(raw ?? undefined);
}

export function serializeStepCopyOverrides(overrides: StepCopyOverrides | undefined): Record<string, unknown> | undefined {
  if (!overrides) return undefined;
  const out: Record<string, unknown> = {};
  for (const [key, row] of Object.entries(overrides)) {
    if (!row || typeof row !== 'object') continue;
    const entry: Record<string, string> = {};
    if (typeof row.title === 'string' && row.title.trim()) entry.title = row.title.trim();
    if (typeof row.hint === 'string' && row.hint.trim()) entry.hint = row.hint.trim();
    if (typeof row.icon === 'string' && row.icon.trim()) entry.icon = row.icon.trim();
    if (Object.keys(entry).length) out[key] = entry;
  }
  return Object.keys(out).length ? out : undefined;
}

export function resolveStepCopy(
  stepKey: string,
  schema?: Record<string, unknown> | null,
): StepCopy {
  const base = CFG_STEP_COPY[stepKey] ?? { icon: '◈', title: stepKey, hint: '' };
  const o = readStepCopyOverrides(schema)[stepKey];
  if (!o || typeof o !== 'object') return base;
  return {
    icon: typeof o.icon === 'string' && o.icon.trim() ? o.icon.trim() : base.icon,
    title: typeof o.title === 'string' && o.title.trim() ? o.title.trim() : base.title,
    hint: typeof o.hint === 'string' && o.hint.trim() ? o.hint.trim() : base.hint,
  };
}

/** Порядок шагов в админке «Тексты». */
export const CFG_STEP_KEYS = Object.keys(CFG_STEP_COPY) as Array<keyof typeof CFG_STEP_COPY>;

export const CFG_STEP_ADMIN_LABELS: Record<string, string> = {
  tier: 'Профиль исполнения (Standard / Premium)',
  power: 'Питание мотора (220В / АКБ)',
  opening: 'Направление открывания',
  control: 'Управление и подключение',
  profile_color: 'Цвет профиля',
  motor_color: 'Цвет мотора',
  size: 'Размер проёма',
};

function mergeStepCopyOverrides(
  global: StepCopyOverrides | null | undefined,
  subtype: StepCopyOverrides,
): StepCopyOverrides {
  const merged: StepCopyOverrides = { ...(global ?? {}) };
  for (const [key, row] of Object.entries(subtype)) {
    if (!row || typeof row !== 'object') continue;
    merged[key] = { ...merged[key], ...row };
  }
  return merged;
}

/** Глобальные тексты из админки + переопределения подтипа в option_schema.step_copy. */
export function buildMergedStepCopySchema(
  global: StepCopyOverrides | null | undefined,
  selectorSchema?: Record<string, unknown> | null,
): Record<string, unknown> | undefined {
  const subtype = parseStepCopyOverrides(selectorSchema);
  const merged = mergeStepCopyOverrides(global, subtype);
  if (!Object.keys(merged).length) return selectorSchema ?? undefined;
  return { ...(selectorSchema ?? {}), step_copy: merged };
}
